/**
 * Right Side Panel Component
 *
 * Always-on context-aware sidebar showing:
 * - Channels: Members | Media | Files | Links tabs
 * - DMs: Profile card + Media | Files | Links tabs
 * - Overlay views (thread, bookmarks, recordings) take priority when active
 */

import React, { useState, useMemo } from 'react';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';
import { useChannel } from '@/hooks/useChannels';
import { useDMThread, useCreateDMThread } from '@/hooks/useDMs';
import { ThreadView } from '@/components/messages/ThreadView';
import { BookmarkPanel } from '@/components/bookmarks';
import { RecordingsPanel } from '@/components/recordings/RecordingsPanel';
import Avatar from '@/components/common/Avatar';
import { formatLastSeen, statusDotColor, STATUS_LABELS, effectiveStatus } from '@/utils/presence.utils';
import { usePresenceStore } from '@/store/presence.store';

interface PanelMember {
  id: string;
  email?: string;
  display_name?: string;
  avatar_url?: string | null;
  last_seen?: string;
}
import { RightSidebarTabs } from './RightSidebarTabs';
import { DMProfileCard } from './DMProfileCard';
import { MediaTabContent } from './MediaTabContent';
import type { SidebarTab } from './RightSidebarTabs';

const PANEL_CLASSES = 'w-80 bg-[#fafbfc] dark:bg-dark-bg-secondary border-l border-gray-200/60 dark:border-dark-border-primary/40 flex-shrink-0 overflow-hidden flex flex-col';

export const RightPanel: React.FC = () => {
  const {
    activeChannelId,
    activeDMThreadId,
    activeThreadMessageId,
    rightPanelView,
    setRightPanelView,
    rightSidebarTab,
    setRightSidebarTab,
  } = useAppStore();
  const { data: channel } = useChannel(activeChannelId);
  const { data: dmThread } = useDMThread(activeDMThreadId);

  // Overlay: Thread view takes priority
  if (rightPanelView === 'thread' || activeThreadMessageId) {
    return (
      <div className={PANEL_CLASSES}>
        <OverlayHeader title="Нишка" onBack={() => setRightPanelView(null)} />
        <ThreadView />
      </div>
    );
  }

  // Overlay: Bookmarks
  if (rightPanelView === 'bookmarks') {
    const handleNavigateToMessage = (messageId: string) => {
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('bg-blue-100', 'dark:bg-blue-900/20');
        setTimeout(() => {
          element.classList.remove('bg-blue-100', 'dark:bg-blue-900/20');
        }, 2000);
      }
    };

    return (
      <div className={PANEL_CLASSES}>
        <BookmarkPanel
          onClose={() => setRightPanelView(null)}
          onNavigateToMessage={handleNavigateToMessage}
        />
      </div>
    );
  }

  // Overlay: Recordings
  if (rightPanelView === 'recordings') {
    return (
      <div className={PANEL_CLASSES}>
        <RecordingsPanel onClose={() => setRightPanelView(null)} />
      </div>
    );
  }

  // DM: Profile + Media/Files/Links tabs
  if (dmThread?.members && dmThread.members.length > 0) {
    const showProfile = !dmThread.is_group && dmThread.other_user;
    const dmTab = rightSidebarTab === 'members' && !dmThread.is_group ? 'media' : rightSidebarTab;

    return (
      <div className={PANEL_CLASSES}>
        {showProfile && <DMProfileCard user={dmThread.other_user!} />}
        {dmThread.is_group && (
          <div className="px-4 py-3 border-b border-dark-border-primary/50">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              Членове ({dmThread.members.length})
            </h3>
            <div className="space-y-2">
              {dmThread.members.map((member) => (
                <div key={member.id} className="flex items-center gap-2">
                  <Avatar user={member} size="xs" />
                  <span className="text-sm text-dark-text-secondary truncate">
                    {member.display_name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <RightSidebarTabs
          activeTab={dmTab}
          onTabChange={(tab) => setRightSidebarTab(tab)}
          showMembers={dmThread.is_group || false}
          counts={dmThread.is_group ? { members: dmThread.members.length } : undefined}
        />
        <div
          id="sidebar-tabpanel"
          role="tabpanel"
          aria-labelledby={`sidebar-tab-${dmTab}`}
          tabIndex={0}
          className="flex-1 overflow-y-auto"
        >
          <MediaTabContent
            tab={dmTab as Exclude<SidebarTab, 'members'>}
            dmThreadId={activeDMThreadId}
          />
        </div>
      </div>
    );
  }

  // Channel: Members | Media | Files | Links tabs
  if (channel) {
    const memberCount = (channel.members as unknown as PanelMember[])?.length || 0;

    return (
      <div className={PANEL_CLASSES}>
        <RightSidebarTabs
          activeTab={rightSidebarTab}
          onTabChange={(tab) => setRightSidebarTab(tab)}
          showMembers={true}
          counts={{ members: memberCount }}
        />
        <div
          id="sidebar-tabpanel"
          role="tabpanel"
          aria-labelledby={`sidebar-tab-${rightSidebarTab}`}
          tabIndex={0}
          className="flex-1 overflow-y-auto"
        >
          {rightSidebarTab === 'members' ? (
            <MembersPanelInline />
          ) : (
            <MediaTabContent
              tab={rightSidebarTab as Exclude<SidebarTab, 'members'>}
              channelId={activeChannelId}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Overlay header with back button
 */
const OverlayHeader: React.FC<{ title: string; onBack: () => void }> = ({ title, onBack }) => (
  <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border-primary flex items-center gap-2 bg-white dark:bg-dark-bg-secondary sticky top-0 z-10">
    <button
      onClick={onBack}
      className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors p-1"
      title="Назад"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">{title}</h3>
  </div>
);

/**
 * Members panel without its own header (used inline inside tabbed sidebar)
 */
const MembersPanelInline: React.FC = () => {
  const { activeChannelId, setActiveDMThread } = useAppStore();
  const { user: currentUser } = useAuthStore();
  const { data: channel } = useChannel(activeChannelId);
  const createDMThreadMutation = useCreateDMThread();
  const [searchQuery, setSearchQuery] = useState('');
  const onlineUserIds = usePresenceStore((s) => s.onlineUserIds);
  const userStatuses = usePresenceStore((s) => s.userStatuses);

  const handleMemberClick = async (memberId: string) => {
    if (memberId === currentUser?.id) return;
    try {
      const thread = await createDMThreadMutation.mutateAsync(memberId);
      setActiveDMThread(thread.id);
    } catch (error) {
      console.error('Failed to open DM:', error);
    }
  };

  const { onlineMembers, offlineMembers } = useMemo(() => {
    const members = (channel?.members as unknown as PanelMember[]) || [];
    const filtered = searchQuery
      ? members.filter((m: PanelMember) =>
          (m.display_name || m.email || '')
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      : members;

    const online: PanelMember[] = [];
    const offline: PanelMember[] = [];
    filtered.forEach((member: PanelMember) => {
      if (onlineUserIds.has(member.id)) {
        online.push(member);
      } else {
        offline.push(member);
      }
    });

    const sortByName = (a: PanelMember, b: PanelMember) => (a.display_name || '').localeCompare(b.display_name || '');
    online.sort(sortByName);
    offline.sort(sortByName);

    return { onlineMembers: online, offlineMembers: offline };
  }, [channel?.members, searchQuery, onlineUserIds]);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-dark-border-primary">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Търсене на членове..."
          className="w-full px-3 py-1.5 text-sm bg-gray-100 dark:bg-dark-bg-input border border-gray-200 dark:border-dark-border-secondary rounded-lg text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
        />
      </div>

      {/* Member List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-4">
        {onlineMembers.length > 0 && (
          <div>
            <h4 className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-dark-text-muted px-2 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Онлайн — {onlineMembers.length}
            </h4>
            <div className="space-y-0.5">
              {onlineMembers.map((member: PanelMember) => (
                <div key={member.id} onClick={() => handleMemberClick(member.id)} className="flex items-center space-x-2 px-2 py-2 hover:bg-gray-100/70 dark:hover:bg-dark-bg-hover/50 rounded-lg transition-colors cursor-pointer">
                  <div className="relative flex-shrink-0">
                    <Avatar user={{ id: member.id, email: member.email || '', display_name: member.display_name || 'Unknown', avatar_url: member.avatar_url || null }} size="sm" />
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-50 dark:border-dark-bg-primary ${statusDotColor(true, userStatuses.get(member.id))}`} title={STATUS_LABELS[effectiveStatus(true, userStatuses.get(member.id))]} />
                  </div>
                  <p className="text-sm font-medium truncate text-gray-900 dark:text-dark-text-primary">{member.display_name || 'Unknown User'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {offlineMembers.length > 0 && (
          <div>
            <h4 className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-dark-text-muted px-2 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
              Офлайн — {offlineMembers.length}
            </h4>
            <div className="space-y-0.5">
              {offlineMembers.map((member: PanelMember) => (
                <div key={member.id} onClick={() => handleMemberClick(member.id)} className="flex items-center space-x-2 px-2 py-2 hover:bg-gray-100/70 dark:hover:bg-dark-bg-hover/50 rounded-lg transition-colors cursor-pointer">
                  <div className="relative flex-shrink-0">
                    <Avatar user={{ id: member.id, email: member.email || '', display_name: member.display_name || 'Unknown', avatar_url: member.avatar_url || null }} size="sm" />
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-gray-50 dark:border-dark-bg-primary bg-gray-400" title={formatLastSeen(member.last_seen)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate text-gray-500 dark:text-dark-text-secondary">{member.display_name || 'Unknown User'}</p>
                    <p className="text-xs text-gray-400 dark:text-dark-text-muted truncate">{formatLastSeen(member.last_seen)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {onlineMembers.length === 0 && offlineMembers.length === 0 && (
          <div className="text-center py-8 text-gray-400 dark:text-dark-text-muted text-sm">
            {searchQuery ? 'Няма намерени членове' : 'Няма членове'}
          </div>
        )}
      </div>
    </div>
  );
};
